import React, { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import clsx from "clsx";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { genericErrorSuppressed } from "../../utils/permissionError";

/** The four outcomes a toast reports, plus the escape hatch for a custom card. */
export type ToastType = "success" | "error" | "warning" | "info" | "custom";

export interface ToastItem {
  id: string;
  type: ToastType;
  message?: string;
  /** One short line above the message, when the message alone needs framing. */
  title?: string;
  duration?: number;
  render?: (t: ToastItem) => React.ReactNode;
}

type Listener = (toasts: ToastItem[]) => void;

let listeners: Listener[] = [];
let toasts: ToastItem[] = [];

function notify() {
  listeners.forEach((l) => l([...toasts]));
}

function remove(id: string) {
  toasts = toasts.filter((t) => t.id !== id);
  notify();
}

/**
 * The countdown lives in the card, not here: it pauses while the pointer is
 * over the toast, so a message cannot expire out from under someone reading
 * it, and the progress bar stays honest about the time actually left.
 */
function addToast(toast: ToastItem) {
  toasts.push(toast);
  notify();
}

const DEFAULT_DURATION = 4000;

export const toast = {
  success(message: string, duration = DEFAULT_DURATION) {
    addToast({ id: uuidv4(), type: "success", message, duration });
  },

  error(message: string, duration = DEFAULT_DURATION) {
    // A 403 has just been reported with a message that names the missing
    // permission; the caller's own generic "Failed to …" would only muddy it.
    if (genericErrorSuppressed()) return;

    addToast({ id: uuidv4(), type: "error", message, duration });
  },

  warning(message: string, duration = DEFAULT_DURATION) {
    addToast({ id: uuidv4(), type: "warning", message, duration });
  },

  info(message: string, duration = DEFAULT_DURATION) {
    addToast({ id: uuidv4(), type: "info", message, duration });
  },

  custom(
    render: (t: ToastItem) => React.ReactNode,
    options?: { duration?: number },
  ) {
    const id = uuidv4();

    addToast({
      id,
      type: "custom",
      render,
      duration: options?.duration ?? DEFAULT_DURATION,
    });

    return id;
  },

  dismiss(id: string) {
    remove(id);
  },

  subscribe(fn: Listener) {
    listeners.push(fn);
    return () => {
      listeners = listeners.filter((l) => l !== fn);
    };
  },
};

interface UndoToastOptions {
  message: string;
  onUndo: () => void;
  duration?: number;
}

export function undoToast({
  message,
  onUndo,
  duration = 5000,
}: UndoToastOptions) {
  const id = toast.custom(
    (t) => (
      <div className="flex items-center justify-between gap-4 bg-[rgb(var(--card))] border border-[rgb(var(--border))] px-4 py-3 rounded-xl shadow-md">
        <span className="text-sm">{message}</span>

        <button
          onClick={() => {
            onUndo();
            toast.dismiss(t.id);
          }}
          className="text-sm font-medium text-[rgb(var(--text-accent))] hover:underline"
        >
          {i18n.t("toast.undo", { ns: "common" })}
        </button>
      </div>
    ),
    { duration },
  );

  return id;
}

/* ============================================================
   The card
   ============================================================ */

/** Each outcome's glyph and the token pair that colours its badge and bar. */
const VARIANT = {
  success: { Icon: CheckCircle2, token: "success" },
  error: { Icon: XCircle, token: "error" },
  warning: { Icon: AlertTriangle, token: "warning" },
  info: { Icon: Info, token: "info" },
} as const;

/**
 * A toast's life: dismisses it when its time is up, and holds the clock while
 * the pointer or focus is on it. Shared by the standard card and by custom
 * cards, which used to have no clock at all and so never went away.
 */
function useCountdown(item: ToastItem) {
  const [paused, setPaused] = useState(false);

  const duration = item.duration ?? DEFAULT_DURATION;
  const timed = Number.isFinite(duration);

  // Time is tracked rather than re-scheduled, so pausing and resuming cannot
  // hand a toast more life than it started with.
  const remaining = useRef(duration);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (!timed || paused) return;

    startedAt.current = Date.now();
    const handle = window.setTimeout(
      () => toast.dismiss(item.id),
      remaining.current,
    );

    return () => {
      window.clearTimeout(handle);
      remaining.current -= Date.now() - startedAt.current;
    };
  }, [item.id, paused, timed]);

  const hold = useCallback(() => setPaused(true), []);
  const release = useCallback(() => setPaused(false), []);

  return { duration, timed, paused, hold, release };
}

/**
 * A custom card (toast.custom): whatever it draws, it expires like any other
 * toast, waits while it is being read, and can be closed with the x on its
 * corner. The x sits on the corner rather than inside, so it cannot cover a
 * card's own button (the Undo toast has one at its right edge).
 */
function CustomToast({ item }: { item: ToastItem }) {
  const { t } = useTranslation("common");
  const { hold, release } = useCountdown(item);

  return (
    <div
      data-toast
      role="status"
      aria-live="polite"
      onMouseEnter={hold}
      onMouseLeave={release}
      onFocusCapture={hold}
      onBlurCapture={release}
      className="relative"
    >
      {item.render?.(item)}
      <button
        type="button"
        onClick={() => toast.dismiss(item.id)}
        aria-label={t("toast.dismiss", { defaultValue: "Dismiss" })}
        className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-[rgb(var(--border))] bg-[rgb(var(--card))] text-[rgb(var(--text-muted))] shadow-sm transition-colors hover:text-[rgb(var(--text-primary))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))]"
      >
        <X size={12} />
      </button>
    </div>
  );
}

function ToastCard({ item }: { item: ToastItem }) {
  const { t } = useTranslation("common");
  const { duration, timed, paused, hold, release } = useCountdown(item);

  const variant =
    VARIANT[item.type as keyof typeof VARIANT] ?? VARIANT.info;
  const { Icon } = variant;
  const fill = `rgb(var(--toast-${variant.token}))`;
  const ink = `rgb(var(--toast-${variant.token}-ink))`;

  return (
    <div
      data-toast
      role={item.type === "error" ? "alert" : "status"}
      aria-live={item.type === "error" ? "assertive" : "polite"}
      onMouseEnter={hold}
      onMouseLeave={release}
      onFocusCapture={hold}
      onBlurCapture={release}
      className="toast-card relative w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] shadow-lg"
    >
      <div className="flex items-start gap-3 py-3 pl-3 pr-9">
        <span
          aria-hidden
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: fill, color: ink }}
        >
          <Icon size={16} strokeWidth={2.5} />
        </span>

        <span className="min-w-0 pt-0.5">
          {item.title && (
            <span className="block text-sm font-semibold text-[rgb(var(--text-primary))]">
              {item.title}
            </span>
          )}
          <span className="block break-words text-sm leading-snug text-[rgb(var(--text-secondary))]">
            {item.message}
          </span>
        </span>
      </div>

      <button
        type="button"
        onClick={() => toast.dismiss(item.id)}
        aria-label={t("toast.dismiss", { defaultValue: "Dismiss" })}
        className="absolute right-2 top-2 rounded-md p-1 text-[rgb(var(--text-muted))] transition-colors hover:bg-[rgb(var(--card-muted))] hover:text-[rgb(var(--text-primary))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))]"
      >
        <X size={14} />
      </button>

      {/* How long is left, in the outcome's own colour. */}
      {timed && (
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-1"
          style={{ backgroundColor: fill, opacity: 0.18 }}
        >
          <span
            className="toast-progress block h-full origin-left"
            style={{
              backgroundColor: fill,
              animationDuration: `${duration}ms`,
              animationPlayState: paused ? "paused" : "running",
            }}
          />
        </span>
      )}
    </div>
  );
}

/**
 * Every toast on screen, newest at the bottom. Mounted once, at the app root.
 */
export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => toast.subscribe(setItems), []);

  if (!items.length) return null;

  // Rendered straight into <body>, last, on the topmost layer the browser allows: a toast is usually the answer
  // to a button in an open dialog, so no dialog — whatever its z-index or wherever it is mounted — may cover it.
  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 top-4 z-[2147483647] flex flex-col items-center gap-2 px-4"
      // The list announces its own children; the cards carry the live regions.
      aria-live="off"
    >
      {items.map((item) => (
        <div key={item.id} className={clsx("pointer-events-auto toast-enter")}>
          {item.type === "custom" && item.render ? (
            <CustomToast item={item} />
          ) : (
            <ToastCard item={item} />
          )}
        </div>
      ))}
    </div>,
    document.body,
  );
}
